import { useCallback, useEffect, useRef } from 'react';

import { useRoomSync } from '../rooms/useRoomSync';
import { usePlayer } from '../../stores/player';
import { useRoomTuning } from '../../stores/room';
import { type Engine, attach } from './HlsEngine';
import { usePlayReporting } from './usePlayReporting';

/**
 * Owns the one `<audio>` element and keeps it in step with the store.
 *
 * ## The element is created once and never unmounted
 *
 * Mounted by `AppShell` as a sibling of the router outlet, so React keeps it in
 * place across every navigation - which is the entire mechanism behind
 * "playback never stops when you navigate" (HANDOFF §9.1). There is no clever
 * state preservation; there is only this structural fact.
 *
 * Renders nothing visible. `PlayerBar` is the UI, and it reads the same store.
 * Splitting them means the bar can re-render freely without any risk of the
 * element being torn down with it.
 */
export function PlayerHost() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const engineRef = useRef<Engine | null>(null);

  const track = usePlayer((s) => s.track);
  const loadToken = usePlayer((s) => s.loadToken);
  const playing = usePlayer((s) => s.playing);
  const volume = usePlayer((s) => s.volume);
  const muted = usePlayer((s) => s.muted);
  const sync = usePlayer((s) => s._sync);

  /*
   * True from the moment a load tears the old stream down until the new one
   * actually starts.
   *
   * `hls.destroy()` detaches the media element, and an element that loses its
   * source fires `pause` - asynchronously, so it lands *after* this effect has
   * set `playing: true` for the incoming track. Without this flag that stray
   * event wrote `playing: false` over a perfectly good load, the play/pause
   * effect duly paused the element, and the track sat there until somebody
   * pressed play. Which is exactly what it looked like: press pause, press
   * play, and it works.
   */
  const swapping = useRef(false);

  /**
   * Ask the element to play, and tell the store the truth if it will not.
   *
   * A function rather than something the play/pause effect owns, because both
   * the effect *and* a fresh load need to call it - and only one of them used
   * to. See the load effect for why that was the whole bug.
   */
  const start = useCallback(() => {
    const audio = audioRef.current;

    if (!audio) return;

    /*
     * `play()` returns a promise that rejects when autoplay policy blocks it,
     * and an unhandled rejection here is noisy in the console for something
     * entirely expected. Reflected back into the store so the UI shows a paused
     * state rather than lying about playing.
     *
     * `loading` is cleared with it. A rejected play() used to leave the flag set
     * with nothing left to clear it, so the bar span for a track that had
     * quietly given up - half of "stuck in a loading state".
     */
    void audio.play().catch((reason: unknown) => {
      /*
       * An interrupted play() is not a refused one.
       *
       * Calling play() straight after `attachMedia` races hls.js resetting the
       * media source, and the browser rejects the pending promise with an
       * AbortError - "interrupted by a new load request". Treating that as a
       * refusal parked the store at `playing: false` over a stream that was
       * about to be perfectly fine, which is the same silence as before with a
       * different cause. `canplay` picks it up instead.
       *
       * A real refusal - autoplay policy, NotAllowedError - is a different
       * thing, and the UI must stop claiming to play.
       */
      if (reason instanceof DOMException && reason.name === 'AbortError') return;

      swapping.current = false;
      sync({ playing: false, loading: false });
    });
  }, [sync]);

  // ---- load ---------------------------------------------------------------
  useEffect(() => {
    const audio = audioRef.current;

    /*
     * Nothing has been asked to play yet.
     *
     * `loadToken` starts at zero and only `play()` ever bumps it, so this is
     * also what keeps a track restored from localStorage on boot from loading
     * itself - see the note in AppShell about why restoring must not autoplay.
     */
    if (!audio || loadToken === 0) return;

    /*
     * Read now rather than through props.
     *
     * `play()` sets the track synchronously and bumps `loadToken` only after
     * the media session round trip, and the two land in different renders. When
     * this effect also depended on `track?.id` it therefore ran *twice* per
     * track change: once on the intermediate render, against a media token
     * minted for the previous queue, and again once the real token arrived.
     *
     * The first of those attaches a stream the cookie may not grant, and a
     * 403 there is retried silently by hls.js for ever. `loadToken` is bumped
     * last precisely so that one dependency means "everything is ready" - the
     * extra deps quietly undid that.
     */
    const { track: current, mediaBase } = usePlayer.getState();

    if (!current || !mediaBase) return;

    swapping.current = true;
    engineRef.current?.destroy();

    engineRef.current = attach(
      audio,
      `${mediaBase}/hls/${current.id}/master.m3u8`,
      (message) => {
        swapping.current = false;
        sync({ error: message, loading: false, playing: false });
      },
    );

    /*
     * Start it here, rather than leaving it to the play/pause effect.
     *
     * That effect runs on a *change* to `playing`. Pushing a new track into a
     * dock that is already playing does not change it - it was true and stays
     * true - so the effect never re-ran and nothing ever called play() on the
     * newly attached stream. Meanwhile `hls.destroy()` had paused the element.
     *
     * The result was a track loaded to readyState 4 and sitting at 0:00 behind
     * a dock that said it was playing: silence, a spinner, and a listener who
     * has to press pause and then play - which flips `playing` and is the only
     * thing that made the effect fire.
     *
     * Autoplay is permitted because this load always follows a click, so the
     * gesture is still active. Restoring on boot deliberately does not reach
     * here - see the note in AppShell.
     */
    sync({ playing: true });
    start();

    return () => {
      engineRef.current?.destroy();
      engineRef.current = null;
    };
    // `loadToken` alone: it is the one value that is only ever written when a
    // complete, consistent state is ready to be loaded.
  }, [loadToken, sync, start]);

  // ---- play / pause -------------------------------------------------------
  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) return;

    if (playing) {
      start();
    } else {
      audio.pause();
    }
  }, [playing, start]);

  useEffect(() => {
    const audio = audioRef.current;

    if (audio) {
      audio.volume = volume;
      audio.muted = muted;
    }
  }, [volume, muted]);

  /*
   * Play counting (§4.5).
   *
   * Its own hook because it is the one listener here that cares about *elapsed
   * listening* rather than about the element's current state, and mixing that
   * accumulator into the event effect below would make both harder to read.
   */
  usePlayReporting(audioRef);

  /*
   * Following a room (§6.8).
   *
   * Beside the element rather than in the room page, because the audio has to
   * keep following the broadcast after somebody navigates away from it - which
   * is the case §9.1 exists to protect. Does nothing at all when no room is
   * tuned.
   */
  useRoomSync(audioRef);

  // ---- element events ------------------------------------------------------
  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) return;

    const onTime = () => sync({ position: audio.currentTime });
    const onDuration = () => sync({ duration: Number.isFinite(audio.duration) ? audio.duration : 0 });
    const onPlaying = () => {
      swapping.current = false;
      sync({ playing: true, loading: false, error: null });
    };

    /*
     * Enough of the stream is decodable to start.
     *
     * The spinner used to be cleared only by `playing`, which meant anything
     * that loaded successfully but did not begin - a blocked autoplay, a pause
     * arriving mid-load - span for ever. This is the event that actually
     * answers the question the spinner is asking.
     */
    const onCanPlay = () => {
      sync({ loading: false });

      /*
       * The reconciliation that makes the rest of this robust.
       *
       * If the store wants to be playing and the element is not, something ate
       * a play() - an aborted promise, a pause from an engine swap, an ordering
       * this file has not thought of. Rather than enumerate them, ask again at
       * the one moment the answer is guaranteed to be actionable: the stream is
       * decodable and the element is idle.
       */
      if (audio.paused && usePlayer.getState().playing) start();
    };

    const onPause = () => {
      // A pause from tearing the old stream down is not the listener pausing.
      if (swapping.current) return;

      sync({ playing: false });
    };

    const onWaiting = () => sync({ loading: true });
    /*
     * The room decides what comes next, not this element.
     *
     * A track ending in a room is not a cue to advance a local queue - the
     * server has its own idea of what is playing, and calling `next()` here
     * would load whatever happened to be in the dock's queue and silently drop
     * the listener out of the broadcast.
     */
    const onEnded = () => {
      if (useRoomTuning.getState().tuned !== null) return;

      usePlayer.getState().next();
    };

    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('durationchange', onDuration);
    audio.addEventListener('loadedmetadata', onDuration);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('durationchange', onDuration);
      audio.removeEventListener('loadedmetadata', onDuration);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('ended', onEnded);
    };
  }, [sync, start]);

  // ---- seeking -------------------------------------------------------------
  /*
   * The store's `position` is written from `timeupdate` several times a second,
   * so it cannot simply be assigned back to the element - that would fight the
   * playhead constantly. `seek()` bumps a separate ref, and only a deliberate
   * jump moves the element.
   */
  const seekTarget = usePlayer((s) => s.position);
  const lastSeek = useRef(seekTarget);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) return;

    // A gap larger than a second is a seek; anything smaller is the playhead
    // advancing normally.
    if (Math.abs(seekTarget - audio.currentTime) > 1 && Math.abs(seekTarget - lastSeek.current) > 1) {
      audio.currentTime = seekTarget;
    }

    lastSeek.current = seekTarget;
  }, [seekTarget]);

  // ---- lock screen and Bluetooth controls ----------------------------------
  /*
   * §9.1 calls this "the most commonly skipped step and the most noticeable
   * when missing", and that is right: without it, the pause button on somebody's
   * headphones does nothing, and the lock screen shows the browser rather than
   * the track.
   */
  useEffect(() => {
    if (!('mediaSession' in navigator) || !track) return;

    navigator.mediaSession.metadata = new MediaMetadata({
      title: track.title,
      artist: track.owner?.display_name ?? '',
      album: '',
      artwork: track.artwork
        ? [{ src: track.artwork, sizes: '800x800', type: 'image/webp' }]
        : [],
    });

    const player = usePlayer.getState();

    navigator.mediaSession.setActionHandler('play', () => player.toggle());
    navigator.mediaSession.setActionHandler('pause', () => player.toggle());
    navigator.mediaSession.setActionHandler('nexttrack', () => usePlayer.getState().next());
    navigator.mediaSession.setActionHandler('previoustrack', () => usePlayer.getState().previous());
    navigator.mediaSession.setActionHandler('seekto', (details) => {
      if (typeof details.seekTime === 'number') {
        usePlayer.getState().seek(details.seekTime);
      }
    });
  }, [track]);

  useEffect(() => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
    }
  }, [playing]);

  return (
    <audio
      ref={audioRef}
      // Metadata only: a page with a player on it should not start pulling
      // segments for a track nobody has chosen to hear.
      preload="metadata"
      // Not `crossOrigin="use-credentials"`. hls.js fetches segments itself
      // with `withCredentials`, and on the native Safari path the audio lives
      // behind the same cookie without the attribute - which additionally
      // requires a CORS response on the manifest that the native path does not
      // otherwise need.
      hidden
    />
  );
}
